import React, { useState, ChangeEvent, useCallback, useEffect, useRef } from "react";

import { useNavigate } from "react-router-dom";

import { AuthenticationForm } from "@components/ui";
import { Field as FormField } from "@components/ui";

import { 
  ComposeURLWithGetQueryArguments, 
  ComposeURLWithResponseParameters,
  isRedirectionResponse,
  markStateAsUsed,
  scheduleSubmittedReset
} from "@shared/utils";
import { PagePaths } from "@shared/constants";
import { useAuthentication, useOAuth2, useRecaptchaContext } from "@shared/contexts";
import { useTranslation } from "react-i18next";
import { useDocumentTitle } from "@shared/hooks/useDocumentTitle";
import { SecondFactorRequest } from "../services/OAuth2Request";
import { useAuthError } from "@shared/hooks/useAuthError";
import { RedirectionResponse, OpenAPI } from "@shared/services";
import { isDevelopment } from "@shared/utils/environment";
import { TimeForNewVerify } from "@components/ui";

interface FormData {
  secondfactorcode: {
    value: string;
    errors: Set<string>;
  };
}

const SecondFactorPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useDocumentTitle(t("login.secondFactor.pageTitle"));
  const { handleOAuth2Error } = useAuthError();
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [serverErrorPopUp, setServerErrorPopUp] = useState<string | null>(null);
  const submittedResetRef = useRef<number | null>(null);

  const { authData, setAuthData } = useAuthentication();
  const { oauth2Data, state } = useOAuth2();
  const { currentToken: antiBotToken } = useRecaptchaContext();

  const [formData, setFormData] = useState<FormData>({
    secondfactorcode: {
      value: "",
      errors: new Set<string>(),
    },
  });

  // Validate password consistency when page loads
  useEffect(() => {
    // Check if we have a valid password token
    if (!authData.passwordRequestResponse) {
      console.warn("Missing password data, redirecting to password step");
      setAuthData({
        ...authData,
        step: 2,
      });
      navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.PASSWORD));
      return;
    }

    // Check if the password token is still valid
    const passwordResponse = authData.passwordRequestResponse;
    if (passwordResponse) {
      const currentTime = Math.floor(Date.now() / 1000);
      const expirationTime = passwordResponse.expiration_time;
      const bufferTime = 30; // 30 seconds buffer
      
      if (expirationTime <= (currentTime + bufferTime)) {
        console.warn("Password token expired, redirecting to password step");
        setAuthData({
          ...authData,
          step: 2,
          passwordRequestResponse: undefined,
        });
        navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.PASSWORD));
        return;
      }
    }
  }, [authData, setAuthData, navigate]);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (submittedResetRef.current !== null) {
        cancelAnimationFrame(submittedResetRef.current);
      }
    };
  }, []);

  const handleFormData = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    formData.secondfactorcode.value = value;
    setFormData({
      ...formData,
    });
  }, [formData]);

  const mergeOAuth2Parameters = useCallback((redirectionResponse: RedirectionResponse): Record<string, any> => {
    return { 
      ...redirectionResponse.parameters,
      client_id: oauth2Data.client_id,
      redirect_uri: oauth2Data.redirect_uri,
      scope: oauth2Data.scope,
      response_type: oauth2Data.response_type,
      code_challenge: oauth2Data.code_challenge,
      code_challenge_method: oauth2Data.code_challenge_method,
      state: state || undefined,
    };
  }, [oauth2Data, state]);

  const buildFallbackUrl = useCallback((redirectionResponse: RedirectionResponse, mergedParameters: Record<string, any>): string => {
    const baseUrl = OpenAPI.BASE || window.location.origin;
    const location = redirectionResponse.location || '';
    const cleanLocation = location.startsWith('/') ? location.substring(1) : location;
    const searchParams = new URLSearchParams();
    Object.entries(mergedParameters).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        searchParams.append(key, String(value));
      }
    });
    return `${baseUrl}/${cleanLocation}?${searchParams.toString()}`;
  }, []);

  const handleRedirectionResponse = useCallback(async (redirectionResponse: RedirectionResponse) => {
    if (isDevelopment()) {
      console.log('[SecondFactor] Handling redirection response:', redirectionResponse);
    }

    if (state) {
      markStateAsUsed(state);
    }

    const mergedParameters = mergeOAuth2Parameters(redirectionResponse);
    const mergedResponse = { ...redirectionResponse, parameters: mergedParameters };
    
    try {
      const redirectUrl = await ComposeURLWithResponseParameters(mergedResponse);
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('[SecondFactor] Error composing redirect URL:', error);
      window.location.href = buildFallbackUrl(redirectionResponse, mergedParameters);
    }
  }, [state, mergeOAuth2Parameters, buildFallbackUrl]);

  const getPasswordToken = useCallback((): string => {
    const passwordToken = authData.passwordRequestResponse?.access_token;
    if (!passwordToken) {
      throw new Error(t("error.runtime.noIdentityToken"));
    }
    return passwordToken;
  }, [authData.passwordRequestResponse]);

  const routeSecondFactorResponse = useCallback(async (response: any) => {
    const isRedirect = isRedirectionResponse(response);
    if (isRedirect) {
      await handleRedirectionResponse(response as RedirectionResponse);
      return;
    }
    console.error('[SecondFactor] Unexpected response type:', response);
    throw new Error(t('error.runtime.unexpectedSecondFactorResponse'));
  }, [handleRedirectionResponse]);

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    setSubmitted(true);

    try {
      const passwordToken = getPasswordToken();

      const response = await SecondFactorRequest(
        formData.secondfactorcode.value,
        "authenticator_app",
        oauth2Data,
        passwordToken,
        antiBotToken || undefined
      );

      await routeSecondFactorResponse(response);
    } catch (e) {
      const error = JSON.parse(JSON.stringify(e));
      handleOAuth2Error(
        "password",
        error,
        setServerErrorPopUp,
        formData.secondfactorcode.errors
      );
    } finally {
      setIsLoading(false);
      setFormData({ ...formData });

      scheduleSubmittedReset(submittedResetRef, setSubmitted);
    }
  }, [formData, authData, oauth2Data, antiBotToken, getPasswordToken, routeSecondFactorResponse, handleOAuth2Error]);

  const back = useCallback(() => {
    setAuthData({
      ...authData,
      step: 2,
    });

    navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.PASSWORD));
  }, [authData, setAuthData, navigate]);

  const sendCodeEmail = useCallback(() => {
    console.log("send code email");
    // TODO: Implement send code email if needed for other factor types
  }, []);

  return (
    <AuthenticationForm
      title={t("login.secondFactor.title")}
      subtitle={t("login.secondFactor.subtitle")}
      back={back}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      serverErrorPopUp={serverErrorPopUp}
      setServerErrorPopUp={setServerErrorPopUp}
    >
      <div className="mb-4">
        <FormField
          autofocus={true}
          name="secondfactorcode"
          type="text"
          required={true}
          onChange={handleFormData}
          value={formData.secondfactorcode.value}
          errors={formData.secondfactorcode.errors}
          submitted={submitted}
        >
          {t("login.secondFactor.codePlaceholder")}
        </FormField>
      </div>

      <TimeForNewVerify sendCodeEmail={sendCodeEmail} />
    </AuthenticationForm>
  );
};

export default SecondFactorPage;
